/**
 * AI Analysis Module
 *
 * This module provides functions to generate AI analysis data from financial reports.
 * It includes a self-contained implementation of the necessary parsing and transformation
 * functions to avoid dependencies on other modules.
 *
 *
 *
 *
 *
 *
 */
import { PLNode } from '../../Dashboard/DParser/types';
import { identifyAccountType } from '../../Dashboard/DParser/DAccountMatching';
import { KSTransaction } from './transactions/types';
import { sumCategories, getWeekNumber } from 'presentation/components/Dashboard/DParser/DchartData';
import { ParsedReport } from '../../Dashboard/DParser/types';
import {
  calculateMetrics,
  calculateStatistics,
  findSignificantChanges,
  normalizeAccountName,
} from '../../Dashboard/DParser/DPLAnalysis/analysis';
import { ReportConfiguration, ReportTimeframe } from '@hone-automation/common';
import { generateAIAnalysisDataV2 } from './aiAnalysisV2';
// HoneReportTimeframe is declared globally in declarations.d.ts

// Define our internal types for AI analysis
export interface AccountValue {
  period: string;
  location: string;
  value: number;
}

export interface AIReportChartMetricsUnit {
  expenses: number;
  revenue: number;
}

export interface ChartDataPoint {
  x: string; // Week number in format "W17"
  y: number; // Value for that week
}

export interface AIChartSeries {
  id: string; // Identifier like "revenue" or "expenses"
  name: string; // Display name like "Revenue" or "Expenses"
  data: ChartDataPoint[];
  fillOpacity?: number;
  color?: string;
}

// Our internal version of HierarchicalAccountData for the AI analysis module
export interface HierarchicalAccountData {
  periodId: string;
  periodStart: string;
  periodEnd: string;
  periodType: string;
  revenue: number;
  expenses: number;
  data: {
    sales: {
      total: number;
      accounts: { [key: string]: number };
    };
    cogs: {
      total: number;
      accounts: { [key: string]: number };
    };
    labor: {
      total: number;
      accounts: { [key: string]: number };
    };
    operatingExpenses: {
      total: number;
      accounts: { [key: string]: number };
      controllableExpenses: { total: number; accounts: { [key: string]: number } };
      nonControllableExpenses: { total: number; accounts: { [key: string]: number } };
    };
  };
}

// Types for the AI Analysis data
export interface AIAnalysisData {
  reportId: string;
  timeframe: string;
  data: Record<string, any>;
  statistics: Record<string, any>;
  significantChanges: Record<string, any>;
  transactions?: KSTransaction[]; // Add transactions field with the correct KSTransaction type
}

export interface ParsedColumn {
  values: ParsedValue[];
  start: string;
  end: string;
  location: string;
  id: string;
}

export interface ParsedRow {
  title: string;
  level: number;
  glCode: number | null;
  order: number;
  parentId: string | null;
  category: string;
  categorization: {
    confidence: 'high' | 'medium' | 'low';
    needsReview: boolean;
  };
}

export interface ParsedValue {
  data: number | null;
  rowId: string;
}

/**
 * Parse a P&L report into a structured format
 * This is a self-contained version of parsePLReport from DParser
 */
export function parsePLReport(report: NestedHoneReport): ParsedReport | null {
  if (!report) return null;

  const parsedReport: ParsedReport = {
    reportId: report.id || '',
    timeframe: report.timeframe || 'Weekly',
    columns: {},
    rows: {},
  };

  // Create column IDs once
  const columnIds = report.dates.map(() => crypto.randomUUID());
  //console.log(report)
  // Initialize columns with the pre-generated IDs
  report.dates.forEach((date, index) => {
    const columnId = columnIds[index];
    if (!date.start || !date.end || !date.location?.name) return;
    parsedReport.columns[columnId] = {
      values: [],
      start: date.start,
      end: date.end,
      location: date.location.name, // Use location name as string
      id: columnId,
    };
  });

  let orderCounter = 0;

  function collectValues(
    node: PLNode,
    parentId: string | null = null,
    parentCategory: string | null = null,
    parentGLCode: number | null = null,
    isExpenses: 'controllable' | 'nonControllable' | null = null
  ): string {
    const rowId = crypto.randomUUID();
    const { category, confidence } = identifyAccountType(
      node.title,
      node.glCode ?? null,
      parentCategory,
      parentGLCode,
      isExpenses
    );
    parsedReport.rows[rowId] = {
      title: node.title,
      level: node.level,
      glCode: node.glCode ?? null,
      order: orderCounter++,
      parentId,
      category,
      categorization: {
        confidence,
        needsReview: confidence === 'low',
      },
    };

    report.dates.forEach((date, dateIndex) => {
      if (!date.start || !date.end || !date.location?.name) return;
      parsedReport.columns[columnIds[dateIndex]].values.push({
        data: node.data[dateIndex] ?? null,
        rowId,
      });
    });

    if (node.sections && node.sections.length > 0) {
      node.sections.forEach(section => {
        {
          section.display !== 'hidden' && collectValues(section, rowId, category, node.glCode, isExpenses);
        }
      });
    }

    return rowId;
  }

  report.sections.forEach(section => {
    const isExpenses =
      section.level === 0 && section.title.toLowerCase().includes('controllable expenses')
        ? section.title.toLowerCase().includes('non-controllable')
          ? 'nonControllable'
          : 'controllable'
        : null;
    section.display !== 'hidden' && collectValues(section as PLNode, null, null, null, isExpenses);
  });
  return parsedReport;
}

/**
 * Generate AI analysis data from a report
 */
export function generateAIAnalysisData(
  report: NestedHoneReport,
  weekStart?: WeekDay,
  reportCFG?: ReportConfiguration,
  requestedTimeframe?: ReportTimeframe
): AIAnalysisData | null {
  return generateAIAnalysisDataV2(report, weekStart, reportCFG, requestedTimeframe);
}
